import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Card, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Alert, Chip, Divider, Button,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { API_BASE_URL } from "../../config";

function buildNotifications(tenants) {
  const now = new Date();
  const notifs = [];

  for (const t of tenants) {
    // Expired
    if (t.status === "expired" || (t.contract_end && new Date(t.contract_end) < now && t.status !== "suspended")) {
      notifs.push({
        id: `exp-${t.id}`,
        severity: "error",
        title: `${t.org_name} — Licence Expired`,
        detail: `Contract ended ${t.contract_end ? new Date(t.contract_end).toLocaleDateString() : "unknown"}. Action required.`,
        tenant: t,
      });
    } else if (t.contract_end) {
      const daysLeft = Math.ceil((new Date(t.contract_end) - now) / 86400000);
      if (daysLeft <= 14) {
        notifs.push({
          id: `soon14-${t.id}`,
          severity: "warning",
          title: `${t.org_name} — Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`,
          detail: `Licence expires on ${new Date(t.contract_end).toLocaleDateString()}. Consider renewing.`,
          tenant: t,
        });
      } else if (daysLeft <= 30) {
        notifs.push({
          id: `soon30-${t.id}`,
          severity: "warning",
          title: `${t.org_name} — Expires in ${daysLeft} days`,
          detail: `Licence expires on ${new Date(t.contract_end).toLocaleDateString()}.`,
          tenant: t,
        });
      }
    }

    // Seat threshold
    if (t.seat_count > 0 && t.seats_used != null) {
      const pct = Math.round((t.seats_used / t.seat_count) * 100);
      const threshold = t.warning_threshold || 80;
      if (pct >= 100) {
        notifs.push({
          id: `seat-full-${t.id}`,
          severity: "error",
          title: `${t.org_name} — Seat Limit Reached`,
          detail: `${t.seats_used}/${t.seat_count} seats used (100%). New users cannot be created.`,
          tenant: t,
        });
      } else if (pct >= threshold) {
        notifs.push({
          id: `seat-warn-${t.id}`,
          severity: "warning",
          title: `${t.org_name} — High Seat Usage (${pct}%)`,
          detail: `${t.seats_used}/${t.seat_count} seats used. Threshold: ${threshold}%.`,
          tenant: t,
        });
      }
    }

    // Suspended tenants
    if (t.status === "suspended") {
      notifs.push({
        id: `susp-${t.id}`,
        severity: "info",
        title: `${t.org_name} — Suspended`,
        detail: `This tenant is currently suspended and users cannot log in.`,
        tenant: t,
      });
    }
  }

  // Sort: errors first, then warnings, then info
  const order = { error: 0, warning: 1, info: 2, success: 3 };
  return notifs.sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9));
}

const SICON = {
  error:   <ErrorOutlineIcon sx={{ color: "#d32f2f" }} />,
  warning: <WarningAmberIcon sx={{ color: "#ed6c02" }} />,
  info:    <InfoOutlinedIcon sx={{ color: "#1565c0" }} />,
  success: <CheckCircleOutlineIcon sx={{ color: "#2e7d32" }} />,
};
const SBORDER = { error: "#fdecea", warning: "#fff3e0", info: "#e3f2fd", success: "#e8f5e9" };
const SCHIP = {
  error:   { bg: "#fdecea", color: "#c62828" },
  warning: { bg: "#fff3e0", color: "#e65100" },
  info:    { bg: "#e3f2fd", color: "#1565c0" },
  success: { bg: "#e8f5e9", color: "#2e7d32" },
};

export default function NotificationsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE_URL}/superadmin/tenants`)
      .then((res) => setTenants(res.data.tenants || []))
      .catch(() => setError("Failed to load tenant data."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  const notifs = buildNotifications(tenants);

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            Licence expiry alerts and seat usage warnings across all tenants
          </Typography>
        </Box>
        <Chip
          label={`${notifs.length} alert${notifs.length !== 1 ? "s" : ""}`}
          color={notifs.some(n => n.severity === "error") ? "error" : notifs.length > 0 ? "warning" : "success"}
          sx={{ fontWeight: 700 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {notifs.length === 0 ? (
        <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 4, textAlign: "center", bgcolor: "#fff" }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#2e7d32", mb: 1 }} />
          <Typography fontWeight={600} color="#2e7d32">All clear</Typography>
          <Typography fontSize="13px" color="text.secondary" mt={0.5}>
            No expiry or seat warnings at this time.
          </Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
          <List disablePadding>
            {notifs.map((n, idx) => {
              const chipStyle = SCHIP[n.severity];
              return (
                <React.Fragment key={n.id}>
                  {idx > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 2,
                      px: 3,
                      bgcolor: SBORDER[n.severity] + "66",
                      alignItems: "flex-start",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.3 }}>
                      {SICON[n.severity]}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                          <Typography fontWeight={600} fontSize="14px">{n.title}</Typography>
                          <Chip
                            label={n.severity}
                            size="small"
                            sx={{
                              bgcolor: chipStyle.bg, color: chipStyle.color,
                              fontSize: "10px", fontWeight: 700, textTransform: "capitalize",
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography fontSize="13px" color="text.secondary">{n.detail}</Typography>
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        textTransform: "none", ml: 2, whiteSpace: "nowrap", alignSelf: "center",
                        color: "#0094FB", borderColor: "#0094FB",
                      }}
                      href={`/platform/tenants/${n.tenant.id}`}
                    >
                      View Tenant
                    </Button>
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </Card>
      )}
    </Box>
  );
}
